import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useGame } from '../context/GameContext';
import TileBoard from '../components/TileBoard';
import PlayerHand from '../components/PlayerHand';
import DiscardPile from '../components/DiscardPile';
import ScoreBoard from '../components/ScoreBoard';
import GameControls from '../components/GameControls';
import GameModal from '../components/GameModal';
import BrowserCompatibilityCheck from '../components/BrowserCompatibilityCheck';

const GameScreen = () => {
  const { 
    gameState, 
    highScore,
    startGame, 
    resetGame, 
    selectTile,
    discardTile,
    drawTile 
  } = useGame();
  
  const [showHelp, setShowHelp] = useState(false);
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [showGameOver, setShowGameOver] = useState(false);
  const [compatibilityResults, setCompatibilityResults] = useState(null);
  const [showCompatibilityWarning, setShowCompatibilityWarning] = useState(false);
  
  // 브라우저 호환성 체크 결과 처리 - 개선된 버전
  const handleCompatibilityCheck = (allPassed, results) => {
    setCompatibilityResults(results);
    
    // 로컬 스토리지가 지원되지 않는 경우에만 경고 표시
    // 다른 기능들은 대체 방법이 있으므로 경고하지 않음
    if (!allPassed && !results.localStorageSupport) {
      setShowCompatibilityWarning(true);
    }
  };
  
  // 게임 종료 체크
  useEffect(() => {
    if (gameState.isPlaying && gameState.tiles.length === 0) {
      setShowGameOver(true);
    }
  }, [gameState.isPlaying, gameState.tiles.length]);
  
  const handleTileClick = (tile, index) => {
    // 플레이어 차례일 때만 타일 선택 가능
    if (gameState.currentPlayer !== 'player') return;
    
    // 타일 선택
    selectTile(tile);
  };
  
  const handleDiscardTile = () => {
    // 선택된 타일이 있을 때만 버리기 가능
    if (!gameState.selectedTile) return;
    
    // 타일 버리기
    discardTile(gameState.selectedTile);
  };
  
  const handleDrawTile = () => {
    // 플레이어 차례일 때만 타일 가져오기 가능
    if (gameState.currentPlayer !== 'player') return;
    
    // 타일 가져오기
    drawTile();
  };
  
  const handleNewGame = () => {
    resetGame();
    startGame();
    setShowGameOver(false);
  };
  
  const handleHelp = () => {
    setShowHelp(true);
  };
  
  const handleToggleSound = () => {
    setIsSoundOn(!isSoundOn);
  };
  
  return (
    <GameContainer>
      {/* 브라우저 호환성 체크 */}
      <BrowserCompatibilityCheck onComplete={handleCompatibilityCheck} />
      
      <GameHeader>
        <GameTitle>모바일 마작 게임</GameTitle>
      </GameHeader>
      
      <GameContent>
        <ScoreBoard score={gameState.score} highScore={highScore} />
        
        <GameControls 
          onNewGame={handleNewGame}
          onHelp={handleHelp}
          isSoundOn={isSoundOn}
          onToggleSound={handleToggleSound}
        />
        
        {gameState.isPlaying ? (
          <>
            <GameInfo>
              <InfoItem>남은 타일: {gameState.remainingTiles}</InfoItem>
              <InfoItem>현재 차례: {gameState.currentPlayer === 'player' ? '플레이어' : '컴퓨터'}</InfoItem>
            </GameInfo>
            
            {gameState.currentPlayer === 'player' && (
              <ActionButtons>
                <ActionButton onClick={handleDrawTile} disabled={gameState.tiles.length === 0}>
                  타일 가져오기
                </ActionButton>
                <ActionButton 
                  onClick={handleDiscardTile} 
                  disabled={!gameState.selectedTile}
                  isDiscard
                >
                  타일 버리기
                </ActionButton>
              </ActionButtons>
            )}
            
            <TileBoard 
              tiles={gameState.tiles.slice(0, 36)} // 예시로 36개만 표시
              onTileClick={() => {}} // 보드의 타일은 클릭할 수 없음
              selectableTiles={[]}
            />
            
            <DiscardPile 
              tiles={gameState.discardPile}
              onTileClick={() => {}} // 버린 패의 타일은 클릭할 수 없음
              selectableTiles={[]}
            />
            
            <PlayerHand 
              tiles={gameState.playerTiles}
              onTileClick={handleTileClick}
              selectableTiles={gameState.playerTiles.map(t => t.id)} // 모든 플레이어 타일 선택 가능
              selectedTile={gameState.selectedTile}
            />
          </>
        ) : (
          <StartScreen>
            <GameLogo>麻雀</GameLogo>
            <GameDescription>
              중국 전통 마작 게임을 모바일에서 즐겨보세요!
            </GameDescription>
            <StartButton onClick={startGame}>게임 시작</StartButton>
          </StartScreen>
        )}
      </GameContent>
      
      {/* 도움말 모달 */}
      <GameModal 
        isOpen={showHelp} 
        title="게임 도움말" 
        onClose={() => setShowHelp(false)}
      >
        <HelpContent>
          <h3>마작 게임 규칙</h3>
          <p>마작은 중국에서 유래한 전통 타일 게임입니다.</p>
          <p>게임의 목표는 같은 종류의 타일을 모아 특정 패턴을 완성하는 것입니다.</p>
          
          <h4>타일 종류</h4>
          <p>- 수패(숫자 타일): 원, 대나무, 문자 (각 1-9)</p>
          <p>- 자패(영예 타일): 바람(동,서,남,북), 용(적,녹,백)</p>
          
          <h4>게임 방법</h4>
          <p>1. 타일을 선택하여 같은 종류의 타일을 모으세요.</p>
          <p>2. 같은 종류의 타일 3개를 모으면 점수를 얻습니다.</p>
          <p>3. 특별한 조합을 완성하면 추가 점수를 얻습니다.</p>
          <p>4. 가능한 많은 점수를 획득하세요!</p>
        </HelpContent>
      </GameModal>
      
      {/* 게임 종료 모달 */}
      <GameModal 
        isOpen={showGameOver} 
        title="게임 종료" 
        onClose={() => setShowGameOver(false)}
      >
        <GameOverContent>
          <h3>게임이 종료되었습니다!</h3>
          <p>최종 점수: {gameState.score}</p>
          <p>최고 점수: {highScore}</p>
          <RestartButton onClick={handleNewGame}>다시 시작</RestartButton>
        </GameOverContent>
      </GameModal>
      
      {/* 호환성 경고 모달 - 개선된 버전 */}
      <GameModal
        isOpen={showCompatibilityWarning}
        title="브라우저 호환성 안내"
        onClose={() => setShowCompatibilityWarning(false)}
      >
        <CompatibilityWarningContent>
          <h3>브라우저 호환성 정보</h3>
          <p>게임은 정상적으로 작동하지만, 일부 기능이 제한될 수 있습니다.</p>
          {compatibilityResults && (
            <ul>
              {!compatibilityResults.localStorageSupport && (
                <li>로컬 스토리지가 지원되지 않습니다. 게임 진행 상황과 최고 점수가 저장되지 않을 수 있습니다.</li>
              )}
              {compatibilityResults.isMobileBrowser && (
                <li className="info">모바일 브라우저가 감지되었습니다. 터치 인터페이스에 최적화되어 있습니다.</li>
              )}
            </ul>
          )}
          <p>게임은 모든 브라우저에서 작동하도록 최적화되었습니다.</p>
          <ContinueButton onClick={() => setShowCompatibilityWarning(false)}>
            게임 시작하기
          </ContinueButton>
        </CompatibilityWarningContent>
      </GameModal>
    </GameContainer>
  );
};

const GameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background-color: #1a4d2e;
  color: #fff;
`;

const GameHeader = styled.header`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  background-color: #143d28;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const GameTitle = styled.h1`
  font-size: 1.2rem;
  margin: 0;
  color: #f8cb66;
`;

const GameContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  overflow-y: auto;
  padding-bottom: 120px; // PlayerHand 컴포넌트 높이만큼 여백
`;

const GameInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  padding: 10px;
  background-color: rgba(20, 61, 40, 0.8);
  border-radius: 8px;
`;

const InfoItem = styled.div`
  font-size: 0.9rem;
  color: #f8cb66;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
  gap: 10px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-weight: bold;
  background-color: ${props => props.isDiscard ? '#e74c3c' : '#f8cb66'};
  color: ${props => props.isDiscard ? 'white' : '#1a4d2e'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const StartScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 2rem;
`;

const GameLogo = styled.div`
  font-size: 4rem;
  font-weight: bold;
  margin-bottom: 2rem;
  color: #f8cb66;
`;

const GameDescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const StartButton = styled.button`
  background-color: #f8cb66;
  color: #1a4d2e;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 1rem 2rem;
  border-radius: 8px;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover, &:active {
    background-color: #f5b945;
    transform: scale(1.05);
  }
`;

const HelpContent = styled.div`
  h3, h4 {
    color: #f8cb66;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
`;

const GameOverContent = styled.div`
  text-align: center;
  
  h3 {
    color: #f8cb66;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
`;

const RestartButton = styled.button`
  background-color: #f8cb66;
  color: #1a4d2e;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover, &:active {
    background-color: #f5b945;
    transform: scale(1.05);
  }
`;

const CompatibilityWarningContent = styled.div`
  text-align: center;
  
  h3 {
    color: #f8cb66;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
  
  ul {
    text-align: left;
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.5rem;
    color: #e74c3c;
    
    &.info {
      color: #3498db;
    }
  }
`;

const ContinueButton = styled.button`
  background-color: #f8cb66;
  color: #1a4d2e;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover, &:active {
    background-color: #f5b945;
    transform: scale(1.05);
  }
`;

export default GameScreen;
